import { css } from 'styled-components';

// Theme detection and color definitions
export const theme = css`
  :root {
    /* Light mode colors (default) */
    --bg-primary: #f4f4f4;
    --bg-secondary: #ffffff;
    --bg-tertiary: #f8f9fa;
    --text-primary: #000000;
    --text-secondary: #333333;
    --text-muted: #666666;
    --border-primary: #d3d6da;
    --border-secondary: #e5e5e5;
    
    /* Game board colors - light mode */
    --board-bg: #f4f4f4;
    --tile-empty: #f4f4f4;
    --tile-filled: #ffffff;
    --tile-border: #d3d6da;
    --tile-text: #000000;
    --tile-correct: #6aaa64;
    --tile-present: #6aaa64;
    --tile-absent: #787c7e;
    
    /* Button colors */
    --button-primary: #6aaa64;
    --button-primary-text: #ffffff;
    --button-primary-hover: #5d9556;
    --button-secondary: #e0e0e0;
    --button-disabled: #d3d6da;
    --button-disabled-text: #787c7e;
    
    /* Keyboard colors */
    --keyboard-bg: #f8f9fa;
    --keyboard-border: #e5e5e5;
    --key-bg: #8a8a8a;
    --key-text: #ffffff;
    --key-hover: #a0a0a0;
    --key-active: #707070;
    --key-disabled: #606060;
    
    /* Input colors */
    --input-bg: #ffffff;
    --input-text: #000000;
    --input-border: #d3d6da;
    --input-border-hover: #b8bcc0;
    --input-disabled: #f5f5f5;
    
    /* Status colors */
    --success-bg: #d4edda;
    --success-text: #155724;
    --success-border: #c3e6cb;
    --error-bg: #f8d7da;
    --error-text: #721c24;
    --error-border: #f5c6cb;
    --focus-color: #007acc;
  }

  @media (prefers-color-scheme: dark) {
    :root {
      /* Dark mode colors */
      --bg-primary: #121213;
      --bg-secondary: #2d2d2d;
      --bg-tertiary: #404040;
      --text-primary: #ffffff;
      --text-secondary: #e0e0e0;
      --text-muted: #aaaaaa;
      --border-primary: #565758;
      --border-secondary: #404040;
      
      /* Game board colors - dark mode */
      --board-bg: #121213;
      --tile-empty: #121213;
      --tile-filled: #565758;
      --tile-border: #3a3a3c;
      --tile-text: #ffffff;
      --tile-correct: #5d9556;
      --tile-present: #5d9556;
      --tile-absent: #3a3a3c;
      
      /* Button colors - dark mode */
      --button-primary: #6aaa64;
      --button-primary-text: #ffffff;
      --button-primary-hover: #5d9556;
      --button-secondary: #404040;
      --button-disabled: #404040;
      --button-disabled-text: #777777;
      
      /* Keyboard colors - dark mode */
      --keyboard-bg: #2d2d2d;
      --keyboard-border: #565758;
      --key-guess-border: #c9b458;
      --key-bg: #606060;
      --key-text: #ffffff;
      --key-hover: #707070;
      --key-active: #505050;
      --key-disabled: #404040;
      
      /* Input colors - dark mode */
      --input-bg: #2d2d2d;
      --input-text: #ffffff;
      --input-border: #565758;
      --input-border-hover: #787c7e;
      --input-disabled: #404040;
      
      /* Status colors - dark mode */
      --success-bg: #1e4620;
      --success-text: #a3d9a5;
      --success-border: #4a7c59;
      --error-bg: #4a1e20;
      --error-text: #f5a5a8;
      --error-border: #7c4a4d;
      --focus-color: #007acc;
    }
  }
`;

// Export individual theme values for JavaScript use
export const getThemeValue = (property) => `var(${property})`;

// Commonly used theme combinations
export const commonStyles = {
  primaryBackground: 'var(--bg-primary)',
  secondaryBackground: 'var(--bg-secondary)',
  primaryText: 'var(--text-primary)',
  secondaryText: 'var(--text-secondary)',
  mutedText: 'var(--text-muted)',
  primaryBorder: 'var(--border-primary)',
  
  // Game specific
  boardBackground: 'var(--board-bg)',
  tileEmpty: 'var(--tile-empty)',
  tileFilled: 'var(--tile-filled)',
  tileBorder: 'var(--tile-border)',
  tileText: 'var(--tile-text)',
  tileCorrect: 'var(--tile-correct)',
  tileAbsent: 'var(--tile-absent)',
  
  // Interactive elements
  buttonPrimary: 'var(--button-primary)',
  buttonSecondary: 'var(--button-primary)',
  buttonPrimaryText: 'var(--button-primary-text)',
  buttonPrimaryHover: 'var(--button-primary-hover)',
  
  // Keyboard
  keyBackground: 'var(--key-bg)',
  keyText: 'var(--key-text)',
  keyHover: 'var(--key-hover)',
  keyGuessBorder: 'var(--key-guess-border)',
  
  // Inputs
  inputBackground: 'var(--input-bg)',
  inputText: 'var(--input-text)',
  inputBorder: 'var(--input-border)',
};